import { useEffect, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  VStack,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { REPORT_LIMITS, type ReportReason } from '@twinden/shared';
import { ApiError, api } from '../../lib/api';
import { REASON_LABEL, REPORT_REASONS, checkReport } from '../../lib/reports';
import { useBlocks } from '../../hooks/useBlocks';
import { useT } from '../../i18n';

// Reporting a member (M58, docs/report-block-contact.md D207): a reason, an
// optional note, and "Also block them" (D208). Ids only: the server can't
// read messages, so nothing of what was said is sent. A bottom sheet on
// phones, a dialog wider up, like the idea sheet.

export interface ReportTarget {
  familyId: string;
  person: { id: string; name: string };
  conversationId?: string | null;
  messageId?: string | null;
}

export function ReportSheet({
  isOpen,
  onClose,
  target,
  onBlocked,
}: {
  isOpen: boolean;
  onClose: () => void;
  target: ReportTarget | null;
  /** They were blocked as part of the report. */
  onBlocked?: (userId: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const { blockedIds, refresh } = useBlocks();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason(null);
    setNote('');
    setAlsoBlock(false);
    setTried(false);
  }, [isOpen, target?.person.id]);

  if (!target) return null;
  const firstName = target.person.name.split(' ')[0] || target.person.name;
  const alreadyBlocked = blockedIds.has(target.person.id);
  const check = checkReport(reason, note);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (!check.ok || !reason) return;
    setSending(true);
    try {
      const response = await api.createReport(target.familyId, {
        reportedUserId: target.person.id,
        reason,
        note: note.trim() || null,
        conversationId: target.conversationId ?? null,
        messageId: target.messageId ?? null,
        block: alsoBlock && !alreadyBlocked,
      });
      toast({ title: t('chat.report.sent'), status: 'success' });
      if (response.data.blocked) {
        await refresh().catch(() => undefined);
        onBlocked?.(target.person.id);
      }
      onClose();
    } catch (err) {
      toast({
        title: t('chat.report.failed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const content = (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <VStack align="stretch" spacing={4}>
        <Heading as="h2" fontSize="22px">
          {t('chat.report.title', { name: target.person.name })}
        </Heading>
        <FormControl isInvalid={tried && !!check.reason}>
          <FormLabel mb={2}>{t('chat.report.reasonLabel')}</FormLabel>
          <RadioGroup
            value={reason ?? ''}
            onChange={(value) => setReason(value as ReportReason)}
          >
            <VStack align="stretch" spacing={2}>
              {REPORT_REASONS.map((value) => (
                <Box
                  key={value}
                  as="label"
                  borderWidth="1px"
                  borderColor={reason === value ? 'accent' : 'line'}
                  bg={reason === value ? 'accent.tint' : 'transparent'}
                  borderRadius="16px"
                  px={4}
                  minH="52px"
                  display="flex"
                  alignItems="center"
                  cursor="pointer"
                >
                  <Radio value={value} colorScheme="brand" size="lg">
                    <Text fontSize="16px" pl={1}>
                      {t(REASON_LABEL[value])}
                    </Text>
                  </Radio>
                </Box>
              ))}
            </VStack>
          </RadioGroup>
          {tried && check.reason && (
            <FormErrorMessage>{t(check.reason.key)}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl isInvalid={!!check.note}>
          <HStack justify="space-between" align="baseline">
            <FormLabel mb={1}>{t('chat.report.noteLabel')}</FormLabel>
            <Text
              fontSize="13px"
              color={check.note ? 'danger' : 'muted'}
              aria-hidden="true"
            >
              {note.trim().length}/{REPORT_LIMITS.NOTE_MAX}
            </Text>
          </HStack>
          <Textarea
            value={note}
            rows={3}
            placeholder={t('chat.report.notePlaceholder')}
            onChange={(event) => setNote(event.target.value)}
          />
          {check.note && (
            <FormErrorMessage>
              {t(check.note.key, check.note.vars)}
            </FormErrorMessage>
          )}
        </FormControl>
        {!alreadyBlocked && (
          <Checkbox
            isChecked={alsoBlock}
            onChange={(event) => setAlsoBlock(event.target.checked)}
            colorScheme="brand"
            size="lg"
            alignItems="flex-start"
          >
            <Text fontWeight={600} fontSize="16px">
              {t('chat.report.alsoBlock', { name: firstName })}
            </Text>
            <Text fontSize="14px" color="muted">
              {t('chat.report.alsoBlockHint')}
            </Text>
          </Checkbox>
        )}
        <Text fontSize="14px" color="muted">
          {t('chat.report.privacy')}
        </Text>
        <HStack justify="flex-end" spacing={2}>
          <Button variant="ghost" colorScheme="gray" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            colorScheme="red"
            isLoading={sending}
            isDisabled={!reason || !!check.note}
          >
            {t('chat.report.send')}
          </Button>
        </HStack>
      </VStack>
    </form>
  );

  if (phone) {
    return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          maxH="92dvh"
          pb="max(env(safe-area-inset-bottom), 16px)"
        >
          <DrawerBody px={5} pt="10px" pb={2} overflowY="auto">
            <Box
              w="40px"
              h="5px"
              borderRadius="full"
              bg="line"
              mx="auto"
              mb={4}
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={5} borderRadius="28px" bg="surface">
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
